import { Box, Divider, Stack, Typography } from '@mui/material'
import { FeatureFlagIdRequestSchema } from 'c1gen/features'
import { C1Chip } from 'c1ui/components/C1Chip'
import { C1Tooltip } from 'c1ui/components/C1Tooltip'
import { FORM_FIELD_KINDS } from 'components/core/dynamic-form/c1FormToYupSchema'
import { C1EmptyIndicator } from 'components/core/empty-indicator/C1EmptyIndicator'
import { C1Icon } from 'components/core/icons/C1Icon'
import { C1MultiAppUserDisplayWithFetch } from 'components/core/multi-app-user/C1MultiAppUserDisplay'
import { RenderPassword } from 'components/core/password/password'
import { C1TextWithLineClamp } from 'components/core/TextWithTruncation/C1TextWithLineClamp'
import { useFeatureChecker } from 'components/no-ui/capability'
import { AppUserDisplayNameWithFetch } from 'components/pages/app/users/app-user-display-name'
import { ResourceDisplayNameWithFetch } from 'components/pages/entitlement/resource'
import { TaskKeyValueMetadataRow } from 'components/pages/task/details/content/TaskKeyValueMetadataRow'
import { TaskValue } from 'lib/actions/task'
import { Check } from 'lucide-react'
import { Field } from 'pbts/c1/api/form/v1/form'
import { Struct } from 'pbts/google/protobuf/struct'
import { FC } from 'react'
import { FormattedMessage, FormattedNumber } from 'react-intl'

// Display-specific field types for rendering request data
enum DisplayFieldType {
    Text = 'text',
    Password = 'password',
    Checkbox = 'checkbox',
    MultiStringChips = 'multi_string_chips',
    TextArea = 'text_area',
    Select = 'select',
    NumberField = 'number',
    AppUserPicker = 'app_user_picker',
    ResourcePicker = 'resource_picker',
    MultiAppUserPicker = 'multi_app_user_picker',
    MultiResourcePicker = 'multi_resource_picker',
}

type TaskRequestedDataProps = {
    task: TaskValue
}

export const TaskRequestedData: FC<TaskRequestedDataProps> = ({ task }) => {
    const hasRequestSchemaFeature = useFeatureChecker(FeatureFlagIdRequestSchema)

    // if the task is in form state dont show the requested data because it will already be in the main actions section
    if (task.policy?.current?.instance?.oneofKind === 'form') {
        return null
    }
    if (!hasRequestSchemaFeature || !task.form?.fields || task.form.fields.length === 0) {
        return null
    }

    return <Stack mt={3} mb={2} spacing={4}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="h4">
                <FormattedMessage id="components_task_details_content_tab_requested_data" defaultMessage="Requested data" />
            </Typography>
        </Stack>
        <Divider sx={{ my: 2 }} />
        <RenderPbStructRequestData data={task.data} fields={task.form.fields} />
    </Stack>
}

type RenderPbStructRequestDataProps = {
    data: Struct | undefined
    fields: Field[]
    renderText?: (fieldId: string, fieldDisplayName: string, value: string) => React.ReactNode
    renderCheckbox?: (fieldId: string, fieldDisplayName: string, value: boolean) => React.ReactNode
    renderMultiStringChips?: (fieldId: string, fieldDisplayName: string, value: string[]) => React.ReactNode
    renderPassword?: (fieldId: string, fieldDisplayName: string, value: string) => React.ReactNode
    renderTextArea?: (fieldId: string, fieldDisplayName: string, value: string) => React.ReactNode
}

// Picker configuration extracted from field definition
type PickerConfig = {
    appId: string
    resourceTypeId?: string
}

type MultiResourceDisplayProps = {
    resourceIds: string[]
    appId: string
    resourceTypeId: string
}

const MultiResourceDisplay: FC<MultiResourceDisplayProps> = ({ resourceIds, appId, resourceTypeId }) => {
    if (resourceIds.length === 0) return <C1EmptyIndicator />

    if (resourceIds.length === 1) {
        return (
            <ResourceDisplayNameWithFetch
                appEntitlement={{ appId, appResourceTypeId: resourceTypeId, appResourceId: resourceIds[0] }}
            />
        )
    }

    const remaining = resourceIds.length - 1

    return (
        <Stack direction="row" alignItems="center" spacing={1}>
            <ResourceDisplayNameWithFetch
                appEntitlement={{ appId, appResourceTypeId: resourceTypeId, appResourceId: resourceIds[0] }}
            />
            <C1Tooltip
                placement="right-end"
                title={
                    <Box margin={1}>
                        <Typography mb={2} variant="subtitle1" component="div">
                            <FormattedMessage id="task_requested_data_resources" defaultMessage="Resources" />
                        </Typography>
                        {resourceIds.slice(1, 15).map((resourceId) => (
                            <Box key={resourceId} marginBottom={2}>
                                <ResourceDisplayNameWithFetch
                                    appEntitlement={{ appId, appResourceTypeId: resourceTypeId, appResourceId: resourceId }}
                                />
                            </Box>
                        ))}
                        {resourceIds.length > 15 && (
                            <Typography variant="caption" color="info.contrastText">
                                <FormattedMessage
                                    id="task_requested_data_more_resources"
                                    defaultMessage="+ {count, plural, one {# other} other {# others}}"
                                    values={{ count: resourceIds.length - 15 }}
                                />
                            </Typography>
                        )}
                    </Box>
                }
            >
                <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
                >
                    <FormattedMessage
                        id="task_requested_data_and_more"
                        defaultMessage="+ {count, plural, one {# more} other {# more}}"
                        values={{ count: remaining }}
                    />
                </Typography>
            </C1Tooltip>
        </Stack>
    )
}

export const RenderPbStructRequestData: FC<RenderPbStructRequestDataProps> =
    ({ data, fields, renderText, renderCheckbox, renderMultiStringChips, renderPassword, renderTextArea }) => {
        if (!data) {
            data = Struct.create({ fields: {} })
        }

        // Extract picker config from field definition
        const getPickerConfig = (field: Field): PickerConfig | undefined => {
            const stringField = field.type?.oneofKind === FORM_FIELD_KINDS.STRING ? field.type.stringField : undefined
            const stringSliceField = field.type?.oneofKind === FORM_FIELD_KINDS.STRING_SLICE ? field.type.stringSliceField : undefined

            const pickerField = stringField?.view?.oneofKind === 'pickerField'
                ? stringField.view.pickerField
                : stringSliceField?.view?.oneofKind === 'pickerField'
                    ? stringSliceField.view.pickerField
                    : undefined

            if (!pickerField) return undefined

            if (pickerField.type.oneofKind === 'appUserPicker') {
                return { appId: pickerField.type.appUserPicker.appId }
            }
            if (pickerField.type.oneofKind === 'resourcePicker') {
                return {
                    appId: pickerField.type.resourcePicker.appId,
                    resourceTypeId: pickerField.type.resourcePicker.resourceTypeId,
                }
            }
            return undefined
        }

        // Get select options from field definition
        const getSelectOptions = (field: Field): Array<{ value: string; displayName: string }> => {
            if (field.type?.oneofKind === FORM_FIELD_KINDS.STRING &&
                field.type.stringField?.view?.oneofKind === 'selectField') {
                return field.type.stringField.view.selectField.options || []
            }
            return []
        }

        // Given a request schema field determine its display type
        const getFieldDisplayType = (field: Field): DisplayFieldType | undefined => {
            switch (field.type?.oneofKind) {
                case FORM_FIELD_KINDS.STRING: {
                    const stringViewKind = field.type?.stringField?.view?.oneofKind
                    if (stringViewKind === 'textField') {
                        return field.type?.stringField?.view?.textField?.multiline ? DisplayFieldType.TextArea : DisplayFieldType.Text
                    }
                    if (stringViewKind === 'passwordField') {
                        return DisplayFieldType.Password
                    }
                    if (stringViewKind === 'selectField') {
                        return DisplayFieldType.Select
                    }
                    if (stringViewKind === 'pickerField') {
                        const pickerType = field.type?.stringField?.view?.pickerField?.type?.oneofKind
                        if (pickerType === 'appUserPicker') {
                            return DisplayFieldType.AppUserPicker
                        }
                        if (pickerType === 'resourcePicker') {
                            return DisplayFieldType.ResourcePicker
                        }
                    }
                    return DisplayFieldType.Text
                }
                case FORM_FIELD_KINDS.BOOL:
                    return DisplayFieldType.Checkbox
                case FORM_FIELD_KINDS.STRING_SLICE: {
                    const sliceViewKind = field.type?.stringSliceField?.view?.oneofKind
                    if (sliceViewKind === 'pickerField') {
                        const pickerType = field.type?.stringSliceField?.view?.pickerField?.type?.oneofKind
                        if (pickerType === 'appUserPicker') {
                            return DisplayFieldType.MultiAppUserPicker
                        }
                        if (pickerType === 'resourcePicker') {
                            return DisplayFieldType.MultiResourcePicker
                        }
                    }
                    return DisplayFieldType.MultiStringChips
                }
                case FORM_FIELD_KINDS.INT64:
                    return DisplayFieldType.NumberField
                default:
                    return undefined
            }
        }

        // get the value by looking at the data pb struct
        const getValue = (fieldId: string): string | boolean | number | string[] | null | undefined | (string | null)[] => {
            const field = data.fields[fieldId]
            if (!field) {
                return undefined
            }

            switch (field.kind?.oneofKind) {
                case 'stringValue':
                    return field.kind?.stringValue
                case 'boolValue':
                    return field.kind?.boolValue
                case 'numberValue':
                    return field.kind?.numberValue
                case 'listValue':
                    return field.kind?.listValue?.values?.map((v) => {
                        if (v.kind?.oneofKind === 'stringValue') {
                            return v.kind?.stringValue
                        }
                        return null
                    }).filter(Boolean)
                default:
                    return null
            }
        }

        // this iterate through fields and render the value for each field
        return <Stack spacing={3}>
            {fields.map((field) => {
                const fieldId = field.name
                const view = getFieldDisplayType(field)
                const value = getValue(fieldId)
                const fieldDisplayName = fields.find((f) => f.name === fieldId)?.displayName || fieldId
                const pickerConfig = getPickerConfig(field)
                const selectOptions = getSelectOptions(field)

                switch (view) {
                    case DisplayFieldType.Text:
                        return renderText?.(fieldId, fieldDisplayName, value as string) ||
                            <TaskKeyValueMetadataRow key={fieldId} label={fieldDisplayName} value={value as string} />

                    case DisplayFieldType.Checkbox:
                        return renderCheckbox?.(fieldId, fieldDisplayName, value as boolean) ||
                            <TaskKeyValueMetadataRow
                                key={fieldId}
                                label={fieldDisplayName}
                                value={value === true
                                    ? <FormattedMessage id="task_requested_data_yes" defaultMessage="Yes" />
                                    : <FormattedMessage id="task_requested_data_no" defaultMessage="No" />}
                            />

                    case DisplayFieldType.MultiStringChips:
                        return renderMultiStringChips?.(fieldId, fieldDisplayName, value as string[]) ||
                            <TaskKeyValueMetadataRow
                                key={fieldId}
                                label={fieldDisplayName}
                                value={value && Array.isArray(value) && value.length > 0 ? <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                    {(value as string[])?.map((v) => <C1Chip key={v} color="info" label={v} />)}
                                </Stack> : <C1EmptyIndicator />}
                            />

                    case DisplayFieldType.Password:
                        return renderPassword?.(fieldId, fieldDisplayName, value as string) ||
                            <TaskKeyValueMetadataRow
                                key={fieldId}
                                label={fieldDisplayName}
                                value={<RenderPassword value={value as string} />}
                            />

                    case DisplayFieldType.TextArea:
                        return renderTextArea?.(fieldId, fieldDisplayName, value as string) ||
                            <TaskKeyValueMetadataRow
                                key={fieldId}
                                label={fieldDisplayName}
                                value={value ? <C1TextWithLineClamp lineClamp={3}>{value}</C1TextWithLineClamp> : <C1EmptyIndicator />}
                            />

                    case DisplayFieldType.Select: {
                        const selectedOption = selectOptions.find((opt) => opt.value === value)
                        return <TaskKeyValueMetadataRow
                            key={fieldId}
                            label={fieldDisplayName}
                            value={selectedOption?.displayName || value as string || <C1EmptyIndicator />}
                        />
                    }

                    case DisplayFieldType.NumberField:
                        return <TaskKeyValueMetadataRow
                            key={fieldId}
                            label={fieldDisplayName}
                            value={value !== undefined && value !== null
                                ? <FormattedNumber value={value as number} />
                                : <C1EmptyIndicator />}
                        />

                    case DisplayFieldType.AppUserPicker:
                        return <TaskKeyValueMetadataRow
                            key={fieldId}
                            label={fieldDisplayName}
                            value={value && pickerConfig
                                ? <AppUserDisplayNameWithFetch appId={pickerConfig.appId} id={value as string} />
                                : <C1EmptyIndicator />}
                        />

                    case DisplayFieldType.ResourcePicker:
                        return <TaskKeyValueMetadataRow
                            key={fieldId}
                            label={fieldDisplayName}
                            value={value && pickerConfig?.resourceTypeId
                                ? (
                                    <ResourceDisplayNameWithFetch
                                        appEntitlement={{
                                            appId: pickerConfig.appId,
                                            appResourceTypeId: pickerConfig.resourceTypeId,
                                            appResourceId: value as string,
                                        }}
                                    />
                                )
                                : <C1EmptyIndicator />}
                        />

                    case DisplayFieldType.MultiAppUserPicker:
                        return <TaskKeyValueMetadataRow
                            key={fieldId}
                            label={fieldDisplayName}
                            value={value && Array.isArray(value) && value.length > 0 && pickerConfig
                                ? <C1MultiAppUserDisplayWithFetch userIds={value as string[]} appId={pickerConfig.appId} />
                                : <C1EmptyIndicator />}
                        />

                    case DisplayFieldType.MultiResourcePicker: {
                        const resourceTypeId = pickerConfig?.resourceTypeId
                        return <TaskKeyValueMetadataRow
                            key={fieldId}
                            label={fieldDisplayName}
                            value={value && Array.isArray(value) && value.length > 0 && pickerConfig && resourceTypeId
                                ? (
                                    <MultiResourceDisplay
                                        resourceIds={value as string[]}
                                        appId={pickerConfig.appId}
                                        resourceTypeId={resourceTypeId}
                                    />
                                )
                                : <C1EmptyIndicator />}
                        />
                    }

                    default:
                        return <TaskKeyValueMetadataRow
                            key={fieldId}
                            label={fieldDisplayName}
                            value={<Typography color="text.secondary">
                                <FormattedMessage
                                    id="task_requested_data_unsupported_field_type_view"
                                    defaultMessage="Unsupported field type"
                                />
                            </Typography>}
                        />
                }
            })}
        </Stack>
    }

export const RenderInformationSubmitted: FC<{ savedMessageIsActive: boolean }> = ({ savedMessageIsActive }) => {
    if (!savedMessageIsActive) {
        return null
    }
    return <Stack
        direction="row"
        alignItems="center"
        spacing={1}
        sx={{
            opacity: savedMessageIsActive ? 1 : 0,
            transition: 'opacity 0.3s ease-in-out',
        }}
    >
        <C1Icon icon={Check} sx={{ color: 'success.main' }} />
        <Typography color="success.main">
            <FormattedMessage id="information_submitted_request_data" defaultMessage="Information submitted" />
        </Typography>
    </Stack>
}
